// Copyright (c) Docugami, Inc. All rights reserved.

import { cardShadow } from 'components/cards/CardStyles';
import { ModalLabelEditor } from 'components/cards/LabelEditor';
import { DisabledCss } from 'components/common/DgCommonStyledComponents';
import { DgProgressBar } from 'components/common/DgProgressBar';
import { DocumentMenu } from 'components/doc_hosting/presentation/DocumentMenu';
import { useDocumentSearchBar } from 'components/doc_hosting/presentation/DocumentSearchBar';
import { createFolderId } from 'components/file_management/file_helpers';
import { TextEditorInput } from 'components/sdv/TextEditorInput';
import { ToolType } from 'components/sdv/ToolbarItem';
import { editTokenColor, primaryTheme } from 'components/shell/presentation/AppStyles';
import { IconDescriptor, IconRenderer } from 'components/work_path/IconRenderer';
import { useWorkPathCache } from 'components/work_path/WorkPathCache';
import { IChunkLinking, noOpChunkLinking } from 'hooks/ChunkLinking';
import { useCommandHandler } from 'hooks/CommandHandler';
import { CursorElement } from 'hooks/CursorElement';
import { useFeatureFlag } from 'hooks/FeatureFlags';
import { useModalSelection, useModalSelectionProvider } from 'hooks/ModalSelection';
import { useSdvDnd } from 'hooks/SdvDnd';
import { useSdvResource } from 'hooks/SdvResource';
import { useTelemetryContext } from 'hooks/TelemetryContext';
import { useWaitCursor } from 'hooks/WaitCursor';
import { dgIconNames } from 'hooks/dgIcons';
import { useDocumentDiagnostics } from 'hooks/diagnostics/DocumentDiagnostics';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTextEditCommands } from 'hooks/textEditCommands';
import { MessageId } from 'messaging/MessageTypes';
import { TextEditCommand } from 'messaging/messages/TextEditMessage';
import { ChunkInstance, TokenRange } from 'networking/api';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IExternalCallbacks } from 'sdv/ExternalCallbacks';
import { InteractEvent } from 'sdv/InteractEvent';
import { SdvInterface } from 'sdv/SdvInterface';
import { SelectionFilters } from 'sdv/SelectionFilters';
import { DefaultWorldSpaceConfiguration, scaleValue } from 'sdv/Space';
import { Engine, ISystem, RenderLayerClearBehavior } from 'sdv/engine/Engine';
import { createFocusViewEntity } from 'sdv/engine/EntityFactory';
import { HtmlElementInputSource } from 'sdv/input/HtmlElementInputSource';
import { IInputSource } from 'sdv/input/InputController';
import { ChunkOutlineRenderer } from 'sdv/renderers/ChunkOutlineRenderer';
import { ControlRenderer } from 'sdv/renderers/ControlRenderer';
import { DebugRenderer } from 'sdv/renderers/DebugRenderer';
import { GrabHandleRenderer } from 'sdv/renderers/GrabHandleRenderer';
import { OverlayRenderer } from 'sdv/renderers/OverlayRenderer';
import { PageRenderer } from 'sdv/renderers/PageRenderer';
import { SearchRenderer } from 'sdv/renderers/SearchRenderer';
import { TokenRenderer } from 'sdv/renderers/TokenRenderer';
import { AutoScrollSystem } from 'sdv/systems/AutoScrollSystem';
import { ChunkRenderSystem } from 'sdv/systems/ChunkRenderSystem';
import { ControlBoundsSystem } from 'sdv/systems/BoundsSystem';
import { ControlHitTestSystem } from 'sdv/systems/ControlHitTestSystem';
import { ControlRenderSystem } from 'sdv/systems/ControlRenderSystem';
import { ResizeSystem } from 'sdv/systems/ResizeSystem';
import { ControlSelectionSystem } from 'sdv/systems/ControlSelectionSystem';
import { ControlTreeSystem } from 'sdv/systems/ControlTreeSystem';
import { CursorSystem } from 'sdv/systems/CursorSystem';
import { DragSystem } from 'sdv/systems/DragSystem';
import { ExternalRenderSystem } from 'sdv/systems/ExternalRenderSystem';
import { FocusSystem } from 'sdv/systems/FocusSystem';
import { HighlighterSystem } from 'sdv/systems/HighlighterSystem';
import { LoadPagesSystem } from 'sdv/systems/LoadPagesSystem';
import { MultiSelectSystem } from 'sdv/systems/MultiSelectSystem';
import { OutflowSelectionSystem } from 'sdv/systems/OutflowSelectionSystem';
import { SearchSystem } from 'sdv/systems/SearchSystem';
import { InteractableClickSystem } from 'sdv/systems/InteractableClickSystem';
import { TableChunkRenderSystem } from 'sdv/systems/TableChunkRenderSystem';
import { TableChunkSelectionSystem } from 'sdv/systems/TableChunkSelectionSystem';
import { TextEditSystem } from 'sdv/systems/TextEditSystem';
import { TokenLinesSystem } from 'sdv/systems/TokenLinesSystem';
import { TokenRangeInfoSystem } from 'sdv/systems/TokenRangeInfoSystem';
import { UpdateViewSystem } from 'sdv/systems/UpdateViewSystem';
import { VisiblePagesSystem } from 'sdv/systems/VisiblePagesSystem';
import { folderIdSelector, pagesInfoSelector, selectDocumentReady, selectMappedTokens } from 'store/dgml/dgmlSelectors';
import { dgmlSlice } from 'store/dgml/dgmlSlice';
import { ChunkStructure, isSpanIdAddedChunk } from 'store/dgml/dgmlTypes';
import { FeatureFlags } from 'store/featureFlags/types';
import { frontendSlice } from 'store/frontend/frontendSlice';
import { selectCurrentChunkInstance, selectEditTokenRange, selectSelectedChunkInstance } from 'store/sdvSelectors';
import { setCurrentPageOnScreen, setCurrentSelectedPage, setEditOperation, setExpandRepeatsEnabled } from 'store/sdvSlice';
import styled from 'styled-components';
import { ChunkCreatedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { ChunkResizing } from 'utils/ChunkResizing';
import { SpanIdUtils } from 'utils/SpanIdUtils';
import { TokenRangeUtils } from 'utils/TokenRangeUtils';
import { getSelectedChunkThunk, selectChunkThunk, useChunkSelection } from '../../../../../hooks/chunkSelection';
import { DgmlInstancesSingleton, IDocumentDgml, useDocumentDgml, useTextVersion } from '../../../../../hooks/documentDgml';
import { PageDataDownloader } from '../../../../../sdv/PageDataDownloader';
import { AppState } from '../../../../../store';
import { setSelectionMode, setSelectionModeLocked } from '../../../../../store/documents/actions';
import { CanvasSelectionMode, ModalSelectionArea, ModalSelectionItemType } from '../../../../../store/documents/types';
import { isPageImageLoaded, isPageTokensLoaded } from '../../../../../store/pages/actions';
import { mapCurrentSelectedPage } from '../../../../../store/state_helpers';
import { SelectionModal } from './SelectionModal';
import { MultiSelectChunk } from 'sdv/Resources';
import { ChunkEditor } from 'components/sdv/ChunkEditor';
import { RepeatsEditorWindow } from 'components/sdv/RepeatsEditor';

const DocCanvas = styled.canvas`
    position: absolute;
    top: 0px;
    left: 0px;
`;

const CanvasHeaderContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    width: 100%;
    left: 0;
    top: 0;
`

const CanvasHeaderAnchor = styled.div`
    display: flex;
    position: relative;
    z-index: 1;
    width: 100%;
    justify-content: center;
`

const SearchBarContainer = styled.div`
    justify-content: flex-end;
    display: flex;
    margin-right: 20px;
`;

const SdvViewContainer = styled.div<{ disabled?: boolean }>`
    height: 100%;
    position: relative;
    scrollbar-gutter: stable;
    max-width: 1000px;
    top: 0;
    left: 0;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    overflow: auto;
    ${props => props.disabled && DisabledCss};
`

const SdvView = styled.div`
    width: 850px;
`

const CanvasContainer = styled.div`
    position: sticky;
    top: 0;
    left: 0;
    pointer-events: none;
`

const RootContainer = styled.div<{ disabled?: boolean }>`
    height: 100%;
    display: flex;
    justify-content: center;
    overflow: hidden;
    flex-direction: column;
    ${props => !!props.disabled && DisabledCss};
`

const smallChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkSmallChunk,
    height: 358,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}

const largeChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkLargeChunk,
    height: 448,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}

const tableChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkTableChunk,
    height: 448,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}

enum ChunkEditAction {
    None,
    Create,
    Resize
}

interface MultiSelectionProps {
    isActive: boolean;
    multiSelectSpans: string[];
    selectedLabel?: string;
    selectionFilters?: SelectionFilters;
    onComplete?: (spanIds?: string[]) => void;
    onCancel?: () => void;
}

export interface WebDocHostProps {
    documentId: string;
    clusterId: string;
    isFilePreview?: boolean;
    chunkLinkingProps?: IChunkLinking;
    propertiesHidden?: boolean;
    setPropertiesHidden?: (hidden: boolean) => void;
    multiSelectProps?: MultiSelectionProps;
}

const chunkEditItemTypes = [ModalSelectionItemType.Chunk];

export const WebDocHost = (props: WebDocHostProps): JSX.Element => {
    // Local State
    const [isDragging, setIsDragging] = useState(false);
    const [chunkEditAction, setChunkEditAction] = useState(ChunkEditAction.None);

    // Refs
    const sdvInterface = useRef<SdvInterface>();
    const pageImageDownloader = useRef<PageDataDownloader>();
    const pageTokensDownloader = useRef<PageDataDownloader>();
    const engine = useRef<Engine | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>();
    const canvasSecondLayerRef = useRef<HTMLCanvasElement>();
    const scrollerRef = useRef<HTMLDivElement>();
    const sdvViewRef = useRef<HTMLDivElement>();
    const subscriptionRef = useRef<Record<string, () => void>>({});

    const externalCallbacksRef = useRef<IExternalCallbacks>({});
    const selectionModalContainerRef = useRef<HTMLDivElement | null>(null);
    const onDragStartRef = useRef<() => void | undefined>();

    // App State
    const currentDocument = useSelector((state: AppState) => state.documents.indexed[props.documentId]);
    const currentSelectedPage = useSelector((state: AppState) => mapCurrentSelectedPage(state));
    const canvasSelectionMode = useSelector((state: AppState) => state.documents.selectionMode);
    const featureFlags = useSelector((state: AppState) => state.featureFlags.featureFlags);
    const textVersion = useTextVersion(props.documentId);
    const isSample = useAppSelector(state => !!state.clusters.indexed[props.clusterId]?.clusterInfo.isDocugamiCluster)
    const isExpandRepeatsEnabled = useAppSelector(state => !!state.sdv.document?.isExpandRepeatsEnabled);
    const canvasChunkEditorEnabled = featureFlags[FeatureFlags.SdvCanvasEditor];

    const workPathCache = useWorkPathCache();

    const dispatch = useAppDispatch();

    const dgml = useDocumentDgml();
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const editTokenRange = useAppSelector(selectEditTokenRange);

    const currentChunkInstance = useAppSelector(selectCurrentChunkInstance);

    const isDocumentReady = useAppSelector(state => selectDocumentReady(state, props.documentId, props.isFilePreview));
    const { selectChunk, clearSelection } = useChunkSelection();

    const previewChunkSets = useAppSelector(state => state.sdv.document?.previewChunkSets);
    const mappedTokens = useAppSelector(state => selectMappedTokens(state, props.clusterId, createFolderId(props.documentId).fileId));

    const multiSelectActive = !!props.multiSelectProps?.isActive;

    const { isActive: isChunkProviderActive, setItemReady: setChunkProviderItemReady } = useModalSelectionProvider(chunkEditItemTypes, onProvideModalSelectionChunk);
    const { start: startChunkEdit, isActive: isLocalChunkEdit } = useModalSelection(
        onLocalChunkActionConfirmed,
        () => {
            sdvInterface.current?.resize?.end(false);
            clearSelection();
            setChunkEditAction(ChunkEditAction.None);
        });

    const { start: startTextEdit, stop: stopTextEdit, isActive: isTextEditActive } = useModalSelection(undefined, () => dispatch(setSelectionMode(CanvasSelectionMode.Select)));

    const { isLinking, isLinkingChunk, isLinkingEntry, startLinkingChunk, canLinkChunk, errorModal: linkingErrorModal, cancel: cancelLinking } = props.chunkLinkingProps ?? noOpChunkLinking;

    const diagnosticsUx = useDocumentDiagnostics(props.documentId, sdvViewRef.current);

    const context = useMemo(() => ({
        documentId: props.documentId,
        documentSetId: props.clusterId,
        chunkInstanceId: selectedChunkInstance?.spanId
    }), [props.documentId, props.clusterId, selectedChunkInstance?.spanId]);

    useTelemetryContext(context);

    const folderId = folderIdSelector(props.documentId)
    const pagesInfo = useAppSelector(state => !!folderId && !!textVersion ? pagesInfoSelector(folderId.clusterId, folderId.fileId, textVersion)(state)?.data?.value ?? [] : []);

    const loadPageImage = useCallback(async (page: number): Promise<void> => {
        if (!!currentDocument?.clusterDocument && page > 0 && page <= currentDocument.clusterDocument.numberOfPages) {
            const promise = dispatch(frontendSlice.endpoints.getPageImage.initiate({ fileId: folderId!.fileId, page: page }));
            const existing = subscriptionRef.current[promise.queryCacheKey];
            if (!existing) {
                subscriptionRef.current[promise.queryCacheKey] = () => promise.unsubscribe();
            }
            else {
                promise.unsubscribe();
            }

            await promise;
        }
    }, [folderId, dispatch, currentDocument?.clusterDocument]);

    const loadPageTokens = useCallback(async (page: number): Promise<void> => {
        if (!!currentDocument?.clusterDocument && !!textVersion && page > 0 && page <= currentDocument.clusterDocument.numberOfPages) {
            const pageInfo = pagesInfo[page - 1];
            if (!!pageInfo && !pageInfo.tokensInfo) {
                return;
            }

            const promise = dispatch(dgmlSlice.endpoints.getTokenLocations.initiate({ docsetId: folderId!.clusterId, fileId: folderId!.fileId, page: page, version: textVersion }));
            const existing = subscriptionRef.current[promise.queryCacheKey];
            if (!existing) {
                subscriptionRef.current[promise.queryCacheKey] = () => promise.unsubscribe();
            }
            else {
                promise.unsubscribe();
            }

            await promise;
        }
    }, [folderId, dispatch, textVersion, currentDocument?.clusterDocument, pagesInfo]);

    const isImageLoaded = useCallback((page: number) => {
        return !!currentDocument?.clusterDocument && page > 0 && page <= currentDocument.clusterDocument.numberOfPages && dispatch(isPageImageLoaded(folderId!.fileId, page))
    }, [folderId, dispatch, currentDocument?.clusterDocument]);

    const isTokensLoaded = useCallback((page: number) => {
        return !!currentDocument?.clusterDocument && !!textVersion && page > 0 && page <= currentDocument.clusterDocument.numberOfPages && dispatch(isPageTokensLoaded(props.documentId, page, textVersion))
    }, [textVersion, dispatch, props.documentId, currentDocument?.clusterDocument]);

    const { element: searchBarElement, currentIndex: currentSearchIndex, results: searchResults } = useDocumentSearchBar(props.documentId);

    useLayoutEffect(() => {
        const scroller = scrollerRef.current;
        return () => {
            if (!!scroller && !!workPathCache) {
                workPathCache.scrollState[`${props.documentId}:sdv`] = scroller.scrollTop
            }
        }
    }, [props.documentId, workPathCache])

    useEffect(() => {
        if (!!engine.current) {
            engine.current.stop();
            engine.current = null;
            sdvInterface.current = undefined;
            pageImageDownloader.current = undefined;
            pageTokensDownloader.current = undefined;
        }

        if (!props.documentId) {
            return;
        }

        pageImageDownloader.current = new PageDataDownloader(loadPageImage, isImageLoaded);
        pageTokensDownloader.current = new PageDataDownloader(loadPageTokens, isTokensLoaded);
        const systems: ISystem[] = [
            new UpdateViewSystem([canvasRef, canvasSecondLayerRef], scrollerRef, sdvViewRef, DefaultWorldSpaceConfiguration),
            new VisiblePagesSystem(),
            new LoadPagesSystem(pageImageDownloader.current, pageTokensDownloader.current, DefaultWorldSpaceConfiguration),
            new ControlTreeSystem(props.documentId),
            new TokenLinesSystem(),
            new ControlBoundsSystem(),
            new ControlHitTestSystem(),
            new DragSystem(),
            new InteractableClickSystem(),
            new ControlSelectionSystem(),
            new AutoScrollSystem(scrollerRef, workPathCache?.scrollState ?? {}),
            new FocusSystem(scrollerRef),
            new TokenRangeInfoSystem(),
            new ResizeSystem(),
            new HighlighterSystem(),
            new TableChunkSelectionSystem(),
            new SearchSystem(),
            new ControlRenderSystem(),
            new ChunkRenderSystem(),
            new TableChunkRenderSystem(),
            new ExternalRenderSystem(canvasRef),
            new CursorSystem(sdvViewRef),
            new TextEditSystem(),
            new OutflowSelectionSystem(),
            new MultiSelectSystem()
        ];

        const inputSource = new HtmlElementInputSource(sdvViewRef, scrollerRef, DefaultWorldSpaceConfiguration);

        // When our external drag handling starts, we want to ensure we clear our global input capture since this is managed
        // by an external system
        onDragStartRef.current = () => inputSource.cancelGlobalCapture();

        const inputSources: IInputSource[] = [
            inputSource
        ]

        engine.current = new Engine(
            systems, inputSources,
            [
                {
                    renderers: [
                        new ChunkOutlineRenderer(),
                        new PageRenderer(),
                        new SearchRenderer(),
                        new ControlRenderer(),
                        new GrabHandleRenderer(),
                        new DebugRenderer(),
                        new TokenRenderer()
                    ],
                    canvas: canvasRef,
                    clearBehavior: RenderLayerClearBehavior.Always
                },
                {
                    renderers: [
                        new OverlayRenderer()
                    ],
                    canvas: canvasSecondLayerRef,
                    clearBehavior: RenderLayerClearBehavior.Always
                }
            ],
            externalCallbacksRef.current);

        engine.current.run();
        engine.current.queuePreUpdateCommand((entityManager, resources) => resources.set("documentId", props.documentId));

        sdvInterface.current = engine.current.sdvInterface;

        return () => {
            if (!!engine.current) {
                engine.current.stop();
                engine.current = null;
                sdvInterface.current = undefined;
                pageImageDownloader.current = undefined;
                pageTokensDownloader.current = undefined;
            }
        }

    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.documentId]);

    useEffect(() => {
        engine.current?.queuePreUpdateCommand((entityManager, resources) => {
            if (resources.set("selectedEntityName", selectedChunkInstance?.spanId ?? '')) {
                if (!!selectedChunkInstance?.tokenRange) {
                    createFocusViewEntity(entityManager, selectedChunkInstance.tokenRange, true);
                }
            }
        });
    }, [props.documentId, selectedChunkInstance])

    const { dragConnectSource: engineDragRef, isDragging: isSdvDragging, item: sdvDragItem } = useSdvDnd(props.clusterId, props.documentId, engine.current, (instance, label) => !!instance && !isChunkProviderActive && (!!label || canLinkChunk(instance)));
    engineDragRef(sdvViewRef);

    useEffect(() => () => Object.values(subscriptionRef.current).forEach(x => x()), []);

    useEffect(() => {
        if (isSdvDragging) {
            onDragStartRef.current?.();
            selectChunk(sdvDragItem!.instance.spanId!);
            if (canLinkChunk(sdvDragItem!.instance)) {
                startLinkingChunk(sdvDragItem!.instance, true, true);
            }
        }
        else {
            cancelLinking();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSdvDragging])

    useEffect(() => {
        if (!props.documentId) {
            return;
        }

        pageImageDownloader.current!.loadPage = loadPageImage;
        pageImageDownloader.current!.isLoaded = isImageLoaded;
    }, [loadPageImage, isImageLoaded, props.documentId]);

    useEffect(() => {
        if (!props.documentId) {
            return;
        }

        pageTokensDownloader.current!.loadPage = loadPageTokens;
        pageTokensDownloader.current!.isLoaded = isTokensLoaded;
    }, [loadPageTokens, isTokensLoaded, props.documentId]);

    useEffect(() => {
        if (!engine.current) {
            return;
        }

        // Update the pages in the engine
        engine.current.queuePreUpdateCommand((entityManager, resources) => {
            resources.set("rawPages", pagesInfo);
        });
    }, [pagesInfo]);

    const { set: setTool } = useSdvResource('tool', engine.current);
    useEffect(() => {
        setTool(canvasSelectionMode);
    }, [canvasSelectionMode, setTool]);

    const { set: setPreviewChunks } = useSdvResource('previewChunks', engine.current);
    useEffect(() => {
        const chunks: { [id: string]: string } = {};
        for (const set of Object.values(previewChunkSets ?? {})) {
            set.spanIds.forEach(chunkId => { chunks[chunkId] = set.color });
        }
        setPreviewChunks(chunks);
    }, [previewChunkSets, setPreviewChunks]);

    const { set: setMultiSelect } = useSdvResource('multiSelect', engine.current);
    useEffect(() => {
        setMultiSelect(multiSelectActive);
    }, [multiSelectActive, setMultiSelect]);

    const { set: setExternalHighlights } = useSdvResource('externalSelections', engine.current);
    useEffect(() => {
        const spanIds = props.multiSelectProps?.multiSelectSpans ?? [];
        const chunks: MultiSelectChunk[] = [];
        for (const spanId of spanIds) {
            const tokenRange = SpanIdUtils.getTokenRange(mappedTokens, SpanIdUtils.parse(spanId));
            if (!!tokenRange) {
                chunks.push({ spanId: spanId, tokenRange: tokenRange });
            }
        }
        setExternalHighlights(chunks ?? []);
    }, [mappedTokens, setExternalHighlights, props.multiSelectProps?.multiSelectSpans]);

    const { set: setTextVersion } = useSdvResource('textVersion', engine.current);
    useEffect(() => {
        setTextVersion(textVersion)
    }, [textVersion, setTextVersion])

    const { set: setResizeDisabled } = useSdvResource('resizeDisabled', engine.current);
    useEffect(() => {
        setResizeDisabled(isLinkingEntry || canvasSelectionMode === CanvasSelectionMode.TextEdit);
    }, [isLinkingEntry, canvasSelectionMode, setResizeDisabled]);

    const { set: setDgml } = useSdvResource('dgml', engine.current);
    useEffect(() => {
        setDgml(!props.isFilePreview ? dgml : null);
    }, [dgml, props.isFilePreview, setDgml]);

    const { set: setFeatureFlags } = useSdvResource('featureFlags', engine.current);
    useEffect(() => {
        setFeatureFlags(featureFlags);
    }, [featureFlags, setFeatureFlags]);

    const { value: focusedPage } = useSdvResource('focusedPage', engine.current);

    useEffect(() => {
        // When the focused page changes in the engine, update redux
        if (!!focusedPage) {
            dispatch(setCurrentPageOnScreen(focusedPage.pageIndex));
        }
    }, [focusedPage, dispatch, props.documentId]);

    useEffect(() => {
        // Any time the selected page changes, try to scroll to that page
        if (!!scrollerRef.current && engine.current && currentSelectedPage !== -1) {
            const pages = engine.current.resourceView.getView('pages').value;
            const viewWidth = engine.current.resourceView.getView("viewWidth").value;
            const worldWidth = engine.current.resourceView.getView("worldSpace").value.width;

            const targetPage = pages[currentSelectedPage];
            if (!!targetPage) {
                scrollerRef.current.scrollTo({ top: scaleValue(targetPage.position.y, viewWidth / worldWidth), behavior: "auto" });
            }
        }
    }, [currentSelectedPage]);

    const { set: setSelectionFilters } = useSdvResource('selectionFilters', engine.current);
    useEffect(() => {
        let selectionFilters: SelectionFilters | null = null;
        if (!!props.multiSelectProps?.isActive && !!props.multiSelectProps?.selectionFilters) {
            selectionFilters = props.multiSelectProps.selectionFilters;
        }
        else if (isLinkingEntry || canvasSelectionMode === CanvasSelectionMode.TextEdit) {
            selectionFilters = SelectionFilters.Disabled;
        }

        setSelectionFilters(selectionFilters);
    }, [isLinkingEntry, setSelectionFilters, canvasSelectionMode, props.multiSelectProps?.isActive, props.multiSelectProps?.selectionFilters]);

    const { set: setSearchState } = useSdvResource('searchState', engine.current);
    useEffect(() => {
        setSearchState({
            currentIndex: currentSearchIndex,
            results: searchResults
        });
    }, [setSearchState, searchResults, currentSearchIndex]);

    async function onProvideModalSelectionChunk(): Promise<ChunkInstance | undefined> {
        if (!!props.isFilePreview) {
            throw new Error('FilePreview is enabled... we should never reach this spot');
        }

        if (chunkEditAction === ChunkEditAction.Resize) {
            return await resizeChunk();
        }
        else if (chunkEditAction === ChunkEditAction.Create) {
            return await createChunk();
        }
        else {
            // Default is selection
            return selectedChunkInstance ?? undefined;
        }
    }

    function onLocalChunkActionConfirmed(chunk?: ChunkInstance) {
        if (!!chunk) {
            dispatch(selectChunkThunk(chunk.spanId));
        }
    }

    async function resizeChunk(): Promise<ChunkInstance | undefined> {
        const preEdit = dispatch(getSelectedChunkThunk());
        let newTokenRange: TokenRange | undefined;
        if (!!preEdit) {
            newTokenRange = sdvInterface.current?.resize?.get()?.newTokenRange ?? editTokenRange
        }

        if (!preEdit || !newTokenRange) {
            sdvInterface.current?.resize?.end(false);
            return;
        }

        const result = await ChunkResizing.commitResize(preEdit, newTokenRange);
        sdvInterface.current?.resize?.end(true);
        return result;
    }

    async function createChunk(): Promise<ChunkInstance | undefined> {
        if (!sdvInterface.current) {
            return;
        }

        if (!editTokenRange) {
            sdvInterface.current.resize?.end(false);
            return;
        }

        const editedTokenRange = { ...editTokenRange };
        dispatch(setEditOperation());
        let targetChunk = dgml.getInstancesFlat().find(x => TokenRangeUtils.rangesEqual(x.tokenRange!, editedTokenRange));
        targetChunk = await dgml.label({
            label: targetChunk?.chunk?.labels[0]?.value,
            spanId: targetChunk?.spanId ?? DgmlInstancesSingleton.getSpanIdForTokenRange(props.documentId, editedTokenRange),
            dgmlVersion: dgml.version!.value
        });

        sdvInterface.current.resize?.end(true);

        Telemetry.trackEvent<ChunkCreatedSchema>({
            spanId: targetChunk.spanId ?? '',
            eventName: TelemetryEvent.ChunkCreated
        });

        return targetChunk;
    }

    useEffect(() => {
        if (!isChunkProviderActive) {
            dispatch(setSelectionModeLocked(false));
            setChunkEditAction(ChunkEditAction.None);
            dispatch(setSelectionMode(CanvasSelectionMode.Select));

            sdvInterface.current?.resize?.end(false);
        }
    }, [isChunkProviderActive, dispatch])

    useEffect(() => {
        sdvInterface.current?.resize?.end(false);
        setChunkEditAction(ChunkEditAction.None);
    }, [canvasSelectionMode])

    useEffect(() => {
        if (canvasSelectionMode !== CanvasSelectionMode.TextEdit && isTextEditActive) {
            stopTextEdit();
        }
    }, [canvasSelectionMode, isTextEditActive, stopTextEdit])

    useEffect(() => {
        if (isLinkingEntry) {
            setChunkEditAction(ChunkEditAction.None);
            dispatch(setSelectionMode(CanvasSelectionMode.Select));
            dispatch(setSelectionModeLocked(true));
        }
        else {
            setIsDragging(false);
            dispatch(setSelectionModeLocked(false));
        }
    }, [isLinkingEntry, dispatch]);

    useEffect(() => {
        if (!isLinkingChunk && !isLocalChunkEdit) {
            setChunkEditAction(ChunkEditAction.None);
        }
    }, [isLinkingChunk, isLocalChunkEdit]);

    useEffect(() => {
        if (isChunkProviderActive) {
            setChunkProviderItemReady(!!currentChunkInstance);
        }
    }, [currentChunkInstance, setChunkProviderItemReady, isChunkProviderActive])

    useEffect(() => {
        if (isLocalChunkEdit) {
            dispatch(setSelectionModeLocked(true));
        }
    }, [isLocalChunkEdit, dispatch]);

    function onLink() {
        if (!isChunkProviderActive && !!selectedChunkInstance) {
            startLinkingChunk(selectedChunkInstance, true);
        }
    }

    externalCallbacksRef.current.onInteraction = useCallback((action: InteractEvent) => {
        if (multiSelectActive) {
            return;
        }
        switch (action) {
            case InteractEvent.Resize: {
                if (chunkEditAction !== ChunkEditAction.None || isExpandRepeatsEnabled) {
                    return;
                }

                let message = '';
                let title = ''
                if (canvasSelectionMode === CanvasSelectionMode.Highlight) {
                    setChunkEditAction(ChunkEditAction.Create);
                    title = 'Highlighter Mode';
                    message = 'Create a new chunk by confirming your selection.'
                }
                else {
                    setChunkEditAction(ChunkEditAction.Resize);
                    title = 'Resize Mode';
                    message = 'Drag the selection handles to resize the chunk.'
                }

                if (!isChunkProviderActive) {
                    startChunkEdit(
                        ModalSelectionItemType.Chunk,
                        {
                            title: title,
                            defaultMessage: message,
                            selectionReady: !!currentChunkInstance
                        },
                        false,
                        [ModalSelectionArea.Canvas, ModalSelectionArea.SelectedItem]
                    );
                }

                break;
            }

            case InteractEvent.DragStart: {
                if (!!selectedChunkInstance && canLinkChunk(selectedChunkInstance) && !isLinking && !isChunkProviderActive) {
                    startLinkingChunk(selectedChunkInstance, true, true);
                    setIsDragging(true);
                }

                break;
            }

            case InteractEvent.DragEnd: {
                if (isLinking) {
                    cancelLinking();
                    setIsDragging(false);
                }

                break;
            }
        }
    }, [multiSelectActive, canvasSelectionMode, chunkEditAction, isExpandRepeatsEnabled, startChunkEdit, startLinkingChunk, selectedChunkInstance, isChunkProviderActive, canLinkChunk, isLinking, cancelLinking, currentChunkInstance]);

    const canLinkSelectedChunk = useMemo(() =>
        !!selectedChunkInstance && !isChunkProviderActive && !isTextEditActive ?
            canLinkChunk(selectedChunkInstance) : false,
        [selectedChunkInstance, canLinkChunk, isChunkProviderActive, isTextEditActive]);

    const documentSearchBar = (
        <SearchBarContainer>
            {searchBarElement}
        </SearchBarContainer>
    );

    const isSdvDebugEnabled = useFeatureFlag(FeatureFlags.SdvDebugEnabled);
    const excludedTools = useMemo(() => {
        const excludedTools = [];
        if (!!props.isFilePreview || isChunkProviderActive) {
            excludedTools.push(ToolType.Link);
        }

        if (!!props.isFilePreview || !isSdvDebugEnabled) {
            excludedTools.push(ToolType.RepeatsEditor)
        }

        if (!!props.isFilePreview || isSample) {
            excludedTools.push(ToolType.TextEdit);
        }

        return excludedTools;
    }, [props.isFilePreview, isChunkProviderActive, isSdvDebugEnabled, isSample]);

    // We can get external triggers to start text edit for a given instance
    useCommandHandler(MessageId.TextEdit, handleExternalTextEdit);
    function handleExternalTextEdit(payload: TextEditCommand) {
        if (payload.instance.spanId !== selectedChunkInstance?.spanId) {
            // We assume that the target chunk will be selected before attempting to start text edit
            return;
        }

        editText();
    }

    const isTextEditDisabled = !selectedChunkInstance || isLocalChunkEdit || multiSelectActive || isLinking || isTextEditActive || isSample || !!props.isFilePreview;

    function editText() {
        if (isTextEditDisabled) {
            return;
        }

        dispatch(setSelectionMode(CanvasSelectionMode.TextEdit));

        startTextEdit(
            ModalSelectionItemType.None,
            {
                title: "EDIT TEXT",
                defaultMessage: "Select the word that you wish to edit and enter the new text in the field above it.",
                modalColor: editTokenColor
            },
            false,
            [ModalSelectionArea.Canvas])
    }

    const { trigger: triggerTextEdit, isLoading: isTextEditOperationInProgress } = useTextEditCommands(props.documentId);
    useEffect(() => {
        if (!isTextEditOperationInProgress) {
            stopTextEdit();
            dispatch(setSelectionMode(CanvasSelectionMode.Select));
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isTextEditOperationInProgress, dispatch])

    useWaitCursor(isTextEditOperationInProgress);

    async function acceptTextEdit(tokenRange: TokenRange, text: string) {
        await triggerTextEdit(tokenRange, text);
    }

    function cancelTextEdit() {
        stopTextEdit();
        dispatch(setSelectionMode(CanvasSelectionMode.Select));
    }

    function endMultiSelect() {
        dispatch(setSelectionModeLocked(false));
        dispatch(setSelectionMode(CanvasSelectionMode.Select));
        clearSelection();
    }

    function onCompleteMultiSelect(spanIds: string[]) {
        props.multiSelectProps?.onComplete?.(spanIds);
        endMultiSelect();
    }

    function onCancelMultiSelect() {
        props.multiSelectProps?.onCancel?.();
        endMultiSelect();
    }

    return (
        <>
            <RootContainer disabled={isTextEditOperationInProgress}>
                {!!props.isFilePreview && documentSearchBar}
                {!props.isFilePreview && <DocumentMenu editText={editText} textEditDisabled={isTextEditDisabled} documentId={props.documentId} engine={engine.current} searchBar={documentSearchBar} canLink={canLinkSelectedChunk} onLink={onLink} excludedTools={excludedTools} isLinking={isLinkingEntry} setPropertiesHidden={value => props.setPropertiesHidden?.(value)} propertiesHidden={props.propertiesHidden !== false} sdvCanvas={sdvViewRef.current} />}
                <CanvasHeaderAnchor>
                    <CanvasHeaderContainer ref={selectionModalContainerRef}>
                        {!isDocumentReady &&
                            <DgProgressBar height={20} backgroundColor="transparent" />}
                        <SelectionModal documentId={props.documentId} />
                    </CanvasHeaderContainer>
                </CanvasHeaderAnchor>
                <SdvViewContainer disabled={isTextEditOperationInProgress} ref={scrollerRef as React.RefObject<HTMLDivElement>} onScroll={() => {
                    if (currentSelectedPage !== -1) {
                        // Reset the selected page on scroll to allow us to scroll back to it if clicked
                        dispatch(setCurrentSelectedPage(-1))
                    }
                }}>
                    {!isExpandRepeatsEnabled && canvasChunkEditorEnabled && <ChunkEditor
                        documentId={props.documentId}
                        clusterId={props.clusterId}
                        engine={engine.current}
                        viewRef={canvasRef} />}
                    {isTextEditActive && <TextEditorInput documentId={props.documentId} engine={engine.current} onAccept={acceptTextEdit} onCancel={cancelTextEdit} />}
                    <CanvasContainer>
                        <DocCanvas ref={canvasRef as React.RefObject<HTMLCanvasElement>} />
                        <DocCanvas ref={canvasSecondLayerRef as React.RefObject<HTMLCanvasElement>} />
                    </CanvasContainer>
                    <SdvView ref={sdvViewRef as React.RefObject<HTMLDivElement>} />
                </SdvViewContainer>
                {linkingErrorModal}
                {diagnosticsUx}
            </RootContainer>
            <CursorElement isVisible={isDragging} offsetY={22} offsetX={-15}>
                <ChunkDragVisual
                    isVisible={isDragging && isDocumentReady}
                    chunk={selectedChunkInstance}
                    dgml={dgml} />
            </CursorElement>
            <ModalLabelEditor
                isOpen={multiSelectActive && !isExpandRepeatsEnabled}
                label={props.multiSelectProps?.selectedLabel}
                documentId={props.documentId}
                clusterId={props.clusterId}
                engine={engine.current}
                onConfirm={onCompleteMultiSelect}
                onCancel={onCancelMultiSelect} />
            <RepeatsEditorWindow clickTarget={sdvViewRef.current} isOpen={isExpandRepeatsEnabled} onClose={() => dispatch(setExpandRepeatsEnabled(false))} sdvInterface={sdvInterface.current} />
        </>
    )
}

interface ChunkDragVisualProps {
    isVisible: boolean;
    chunk?: ChunkInstance;
    dgml: IDocumentDgml;
}

const ChunkDragVisual = (props: ChunkDragVisualProps): JSX.Element => {
    if (!props.isVisible || !props.chunk || !props.dgml.isInitialized() || isSpanIdAddedChunk(props.chunk.spanId)) {
        return <></>
    }

    let descriptor: IconDescriptor = smallChunkDragIcon;
    const isTable = props.chunk.chunk?.structure?.type === ChunkStructure.Table;
    if (isTable) {
        descriptor = tableChunkDragIcon;
    }
    else if (DgmlInstancesSingleton.isStructuralChunk(props.chunk)) {
        descriptor = largeChunkDragIcon;
    }

    return <IconRenderer source={descriptor} width={30} height={30} />
}
